import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import EthnicityStep from 'src/components/AzarOnly/Login/Steps/Register/EthnicityStep';
import { GenderButton, GenderDesc, LoadingBackground, LoginModalHeader, SelectChildGenderButton } from 'src/components/AzarOnly/Login/styles';
import useRegister from 'src/components/AzarOnly/Login/useRegister';
import Button from 'src/components/Button';
import Gap from 'src/components/Gap';
import LoadingIndicator from 'src/components/LoadingIndicator';
import { ModalContent as ModalContentBase, ModalFooter, ModalLoading, ModalTitle } from 'src/components/ModalLayout';
import { useGetConsents } from 'src/hooks/useGetConsents';
import { socialTokenAtom, tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { childGenderAtom, FALLBACK_SIGNUP_SETTINGS, genderAtom, signUpSettingsLoadableAtom } from 'src/stores/register/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { GENDER, NonBinaryChildGender } from 'src/types/register';
import { pushEvent } from 'src/utils/event-gateway';
import ChildGenderInput from './ChildGenderInput';
const ArrowRight = styled(dynamic(() => import('src/assets/images/icChevronRight.svg')))`
  width: 16px;
  height: 16px;
  fill: ${({
  theme
}) => theme.color.content9};
  ${({
  theme
}) => theme.isRtl && 'transform: scaleX(-1);'}
`;
const NonBinaryText = styled.div`
  display: flex;
  flex-direction: column;
`;
const ChildGenderText = styled.span`
  color: ${({
  theme
}) => theme.color.content9};
  font-size: 12px;
  font-weight: 400;
  margin-top: 2px;
`;
const ModalContent = styled(ModalContentBase)`
  position: relative;
`;
const RegisterGenderStep: React.FC = () => {
  const signUpSettingsLoadable = useAtomValue(signUpSettingsLoadableAtom);
  const signUpSettings = signUpSettingsLoadable.state === 'hasData' ? signUpSettingsLoadable.data : FALLBACK_SIGNUP_SETTINGS;
  const {
    data: consentsData
  } = useGetConsents();
  const {
    uiType: consentsUiType
  } = consentsData?.data?.result || {};
  const [isShowChildGenderFilterInput, setIsShowChildGenderFilterInput] = useState(false);
  const [gender, setGender] = useState<GENDER | undefined>();
  const setGenderInRegisterForm = useSetAtom(genderAtom);
  const [childGender, setChildGender] = useState<NonBinaryChildGender | undefined>();
  const setChildGenderInRegisterForm = useSetAtom(childGenderAtom);
  const [hideGenderOption, setHideGenderOption] = useState<boolean | null>(null);
  const {
    tryRegister,
    pushHistory
  } = useRegister();
  useEffect(() => {
    setHideGenderOption(signUpSettings.genderSettings.hideGenderDefaultValue);
  }, [signUpSettings.genderSettings.hideGenderDefaultValue]);
  const {
    t
  } = useTranslation();
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_GENDER_INFO,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  const [socialToken, setSocialToken] = useAtom(socialTokenAtom);
  const handleGenderSelect = useCallback(async ({
    gender: _gender,
    childGender: _childGender
  }: {
    gender?: GENDER;
    childGender?: NonBinaryChildGender;
  }) => {
    if (hideGenderOption === null) return;
    const target = _gender === GENDER.NONBINARY ? 'non_binary' : _gender?.toLowerCase();
    pushEvent({
      eventName: EVENT_NAME.SIGNUP__SELECT_GENDER,
      eventType: EVENT_TYPE.SIGN_UP,
      eventParams: {
        action_category: 'action',
        tab: 'signup',
        page: 'gender',
        target,
        child_gender: _childGender?.key || target
      }
    });
    setGenderInRegisterForm(_gender);
    setChildGenderInRegisterForm(_childGender);
    if (signUpSettings.ethnicitySettings) {
      pushHistory(<EthnicityStep />);
      return;
    }
    await tryRegister();
  }, [hideGenderOption, pushHistory, tryRegister, setChildGenderInRegisterForm, setGenderInRegisterForm, signUpSettings.ethnicitySettings]);
  useEffect(() => {
    if (!socialToken?.gender || hideGenderOption === null) return;
    setSocialToken({
      ...socialToken,
      gender: undefined
    });
    handleGenderSelect({});
  }, [consentsUiType, hideGenderOption, setSocialToken, socialToken, handleGenderSelect]);
  const handleClickBinary = useCallback((selectedGender: GENDER) => {
    setChildGender(undefined);
    setGender(selectedGender);
    handleGenderSelect({
      gender: selectedGender
    });
  }, [handleGenderSelect]);
  const handleClickNonBinary = useCallback(() => {
    setGender(GENDER.NONBINARY);
    setIsShowChildGenderFilterInput(true);
  }, [setGender]);
  const handleSelectChildGender = (_childGender: NonBinaryChildGender) => {
    setChildGender(_childGender);
    setIsShowChildGenderFilterInput(false);
  };
  const disabledNext = useMemo(() => {
    if (!gender) return true;
    if (gender === GENDER.NONBINARY && !childGender) return true;
    return false;
  }, [childGender, gender]);
  if (socialToken?.gender || hideGenderOption === null) {
    return <LoadingBackground>
        <LoadingIndicator size={18} />
      </LoadingBackground>;
  }
  if (isShowChildGenderFilterInput) {
    return <ChildGenderInput list={signUpSettings.genderSettings.nonBinaryChildGenders} onSelectedChildGender={handleSelectChildGender} onBack={() => setIsShowChildGenderFilterInput(false)} />;
  }
  return <>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <LoginModalHeader data-testid='LoginModal-GenderStep' data-sentry-element="LoginModalHeader" data-sentry-source-file="index.tsx">
          <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="index.tsx">{t('SIGNUP__INFO_GENDER')} </ModalTitle>
        </LoginModalHeader>
        {signUpSettingsLoadable.state !== 'loading' && <>
            <GenderButton onClick={() => handleClickBinary(GENDER.MALE)} inactive={!!gender && gender !== GENDER.MALE} data-testid='LoginModal-GenderStep-Male'>
              {t('gender_male')}
            </GenderButton>
            <GenderButton onClick={() => handleClickBinary(GENDER.FEMALE)} inactive={!!gender && gender !== GENDER.FEMALE}>
              {t('gender_female')}
            </GenderButton>
            {signUpSettings.genderSettings.nonBinaryEnabled && <GenderButton onClick={handleClickNonBinary} inactive={!!gender && gender !== GENDER.NONBINARY}>
                <NonBinaryText>
                  {t('gender_nonbinary')}
                  {gender === GENDER.NONBINARY && <SelectChildGenderButton>
                      {childGender ? <ChildGenderText>{childGender.localizedText}</ChildGenderText> : t('signup_gender_nonbinary_desc')}
                      <Gap width={8} />
                      <ArrowRight />
                    </SelectChildGenderButton>}
                </NonBinaryText>
              </GenderButton>}
          </>}
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button id='sign-up-gender-next' disabled={disabledNext} onClick={() => handleGenderSelect({
        gender,
        childGender
      })} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('SIGNUP__NEXT')}
        </Button>
        <GenderDesc data-sentry-element="GenderDesc" data-sentry-source-file="index.tsx">{t('signup_extra_info_gender_warning')}</GenderDesc>
      </ModalFooter>
    </>;
};
const WithSuspense: React.FC = () => <React.Suspense fallback={<ModalLoading />} data-sentry-element="React.Suspense" data-sentry-component="WithSuspense" data-sentry-source-file="index.tsx">
    <RegisterGenderStep data-sentry-element="RegisterGenderStep" data-sentry-source-file="index.tsx" />
  </React.Suspense>;
export default React.memo(WithSuspense);